/**
 * Extracts the file ID from a Google Drive URL
 * @param url - Google Drive URL (e.g., https://drive.google.com/file/d/FILE_ID/view)
 * @returns The file ID or null if not found
 */
export function extractGoogleDriveFileId(url: string): string | null {
    if (!url) return null;

    // Match patterns:
    // https://drive.google.com/file/d/FILE_ID/view
    // https://drive.google.com/file/d/FILE_ID/edit
    // https://drive.google.com/open?id=FILE_ID
    // https://drive.google.com/uc?id=FILE_ID

    const patterns = [/\/file\/d\/([a-zA-Z0-9_-]+)/, /[?&]id=([a-zA-Z0-9_-]+)/];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }

    // If no pattern matches and input looks like a file ID, return it
    if (/^[a-zA-Z0-9_-]+$/.test(url)) {
        return url;
    }

    return null;
}

/**
 * Generates a Google Drive thumbnail URL from a file ID or Drive URL
 * @param urlOrFileId - Google Drive URL or file ID
 * @param size - Thumbnail size (default: w320)
 * @returns The thumbnail URL or null if invalid
 */
export function getGoogleDriveThumbnailUrl(
    urlOrFileId: string,
    size: string = "w320"
): string | null {
    if (!urlOrFileId) return null;

    const fileId = extractGoogleDriveFileId(urlOrFileId);
    if (!fileId) return null;

    // Use Google Drive's thumbnail API endpoint
    // Note: Files must be publicly accessible for this to work
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=${size}`;
}
